import React, { FC, useMemo } from 'react';
import styled from 'styled-components';

type Props = {
  capacityHours: number;
  allocatedHours: number;
  scheduledHours: number;
  actualHours: number;
};

const BarOuter = styled.div`
  position: relative;
  width: 100%;
  height: 36px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  overflow: hidden;
`;

const BarAllocated = styled.div<{ percent: number }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${p => Math.max(0, Math.min(100, p.percent))}%;
  background: #3a4575;
`;

const BarScheduled = styled.div<{ percent: number }>`
  position: absolute;
  top: 17%;
  left: 0;
  width: ${p => Math.max(0, Math.min(100, p.percent))}%;
  height: 66%;
  background: #00a7f7;
`;

const BarUsed = styled.div<{ percent: number }>`
  position: absolute;
  left: 0;
  width: ${p => Math.max(0, Math.min(100, p.percent))}%;
  height: 32%;
  top: 34%;
  background: #99e6b3;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.25);
  border-radius: 4px;
`;

const SummaryCapacityBar: FC<Props> = ({ capacityHours, allocatedHours, scheduledHours, actualHours }) => {
  const { allocatedPct, scheduledPctCap, usedPctCap } = useMemo(() => {
    const cap = Math.max(0, capacityHours || 0);
    const alloc = Math.max(0, allocatedHours || 0);
    const scheduledRelAlloc = alloc > 0 ? (scheduledHours / alloc) * 100 : 0;
    const actualRelAlloc = alloc > 0 ? (actualHours / alloc) * 100 : 0;

    const allocatedPct = cap > 0 ? (alloc / cap) * 100 : 0;
    const scheduledPctCap = (allocatedPct * Math.max(0, scheduledRelAlloc)) / 100;
    const usedPctCap = (allocatedPct * Math.max(0, actualRelAlloc)) / 100;

    return {
      allocatedPct,
      scheduledPctCap: Math.min(scheduledPctCap, 100),
      usedPctCap: Math.min(usedPctCap, 100),
    };
  }, [capacityHours, allocatedHours, scheduledHours, actualHours]);

  return (
    <BarOuter>
      <BarAllocated percent={allocatedPct} />
      <BarScheduled percent={scheduledPctCap} />
      <BarUsed percent={usedPctCap} />
    </BarOuter>
  );
};

export default SummaryCapacityBar;
